import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  IconButton,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  Person,
  Assignment,
  Analytics,
  Add,
  Edit,
  CheckCircle,
  Schedule,
  AttachMoney,
  Assessment,
  Timeline  // Added for Dynamic Forecasting
} from '@mui/icons-material';

// Import all modal components
import SundayPlanningInterface from './SundayPlanningInterface';
import EndOfDayPerformanceReview from './EndOfDayPerformanceReview';
import DailyForecastUpdate from './DailyForecastUpdate';
import VarianceAnalysisModal from './VarianceAnalysisModal';

const WeeklyForecastDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for all modal components
  const [showSundayPlanning, setShowSundayPlanning] = useState(false);
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);
  const [showDailyUpdate, setShowDailyUpdate] = useState(false);
  const [showVarianceAnalysis, setShowVarianceAnalysis] = useState(false);

  // Dynamic forecasting data
  const [dynamicData, setDynamicData] = useState({
    booking_changes: 0,
    walk_ins: 0,
    latest_forecast: 0,
    forecast_accuracy: 0
  });

  // Custom Pound Icon Component
  const PoundIcon = ({ sx, ...props }) => (
    <Box sx={{ 
      fontWeight: 'bold', 
      fontSize: '1.2rem', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      width: '1em',
      height: '1em',
      color: 'inherit',
      ...sx 
    }} {...props}>
      £
    </Box>
  );

  useEffect(() => {
    fetchCurrentWeekData();
    fetchDynamicForecastData();
  }, []);

  const fetchCurrentWeekData = async () => {
    try {
      console.log('🔄 Starting fetchCurrentWeekData...');
      setLoading(true);
      
      const response = await api.get('/forecast/current-week');
      
      console.log('✅ API Response received:', {
        status: response.status,
        data: response.data,
        hasData: response.data?.has_data,
        totalForecast: response.data?.total_forecast
      });
      
      if (response.data && response.data.has_data) {
        console.log('✅ Setting forecast data:', response.data);
        setForecastData(response.data);
        setError(null);
      } else {
        console.log('⚠️ API returned no data:', response.data);
        setError('No forecast data found for this week.');
      }
      
    } catch (error) {
      console.error('❌ Error in fetchCurrentWeekData:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setError(`API Error: ${error.message}`);
      setForecastData(null);
      
    } finally {
      setLoading(false);
    }
  };

  const fetchDynamicForecastData = async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const startDateStr = startDate.toISOString().split('T')[0];
      
      const response = await api.get(`/forecast/variance-analysis?start_date=${startDateStr}&end_date=${endDate}`);
      
      if (response.data && response.data.success) {
        setDynamicData({
          booking_changes: response.data.summary.total_booking_changes || 0,
          walk_ins: response.data.summary.total_walk_ins || 0,
          latest_forecast: response.data.summary.total_latest_forecast || 0,
          forecast_accuracy: response.data.summary.forecast_accuracy || 0
        });
      }
    } catch (error) {
      console.error('Error fetching dynamic forecast data:', error);
    }
  };

  // Function to refresh data after changes
  const refreshForecastData = async () => {
    await fetchCurrentWeekData();
    await fetchDynamicForecastData();
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'error';
  };

  const getPerformanceIcon = (actual, forecast) => {
    if (actual === null || actual === undefined) return <Schedule />;
    if (actual >= forecast) return <TrendingUp color="success" />;
    return <TrendingDown color="error" />;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return `£${Number(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Enhanced Weekly Overview with Dynamic Forecasting Intelligence
  const WeeklyOverview = () => (
    <Grid container spacing={3}>
      {/* Dynamic Forecasting Intelligence Section */}
      <Grid item xs={12}>
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp sx={{ color: 'primary.main' }} />
              📈 Dynamic Forecasting Intelligence
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {formatCurrency(forecastData?.total_forecast || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Original Sunday Forecast
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(dynamicData.latest_forecast)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Latest Revised Forecast
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(forecastData?.total_actual || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Actual Revenue
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h4" 
                    color={dynamicData.booking_changes >= 0 ? 'success.main' : 'error.main'}
                  >
                    {dynamicData.booking_changes >= 0 ? '+' : ''}
                    {formatCurrency(dynamicData.booking_changes)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Booking Changes
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Variance Breakdown */}
            <Box mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Alert 
                    severity={dynamicData.booking_changes >= 0 ? 'success' : 'warning'}
                    icon={<Timeline />}
                  >
                    <Typography variant="subtitle2">
                      Booking Pattern Analysis
                    </Typography>
                    <Typography variant="body2">
                      {dynamicData.booking_changes >= 0 
                        ? `${formatCurrency(dynamicData.booking_changes)} in additional bookings this week`
                        : `${formatCurrency(Math.abs(dynamicData.booking_changes))} lost to cancellations this week`
                      }
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Alert 
                    severity={dynamicData.walk_ins >= 0 ? 'info' : 'error'}
                    icon={<TrendingUp />}
                  >
                    <Typography variant="subtitle2">
                      Walk-in Performance
                    </Typography>
                    <Typography variant="body2">
                      {dynamicData.walk_ins >= 0 
                        ? `${formatCurrency(dynamicData.walk_ins)} above latest forecasts`
                        : `${formatCurrency(Math.abs(dynamicData.walk_ins))} below latest forecasts`
                      }
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Action Buttons for Dynamic Forecasting */}
      <Grid item xs={12}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={() => setShowSundayPlanning(true)}
            startIcon={<CalendarToday />}
          >
            Sunday Planning
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setShowPerformanceReview(true)}
            startIcon={<Assessment />}
          >
            Performance Review
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => setShowDailyUpdate(true)}
            startIcon={<TrendingUp />}
            sx={{ 
              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
              boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
            }}
          >
            📈 Update Tomorrow's Forecast
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowVarianceAnalysis(true)}
            startIcon={<Analytics />}
          >
            📊 Variance Analysis
          </Button>
        </Box>
      </Grid>

      {/* Weekly Progress Card */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Weekly Progress
              </Typography>
            </Box>
            
            <Typography variant="h3" fontWeight="bold" color="primary.main" gutterBottom>
              {forecastData?.weekly_progress}%
            </Typography>
            
            <LinearProgress
              variant="determinate"
              value={forecastData?.weekly_progress || 0}
              sx={{
                height: 12,
                borderRadius: 6,
                mb: 2,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: forecastData?.weekly_progress >= 80 ? 'success.main' : 
                                  forecastData?.weekly_progress >= 60 ? 'warning.main' : 'error.main'
                }
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Actual: {formatCurrency(forecastData?.total_actual)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Target: {formatCurrency(forecastData?.total_forecast)}
              </Typography>
            </Box>
            
            <Chip
              label={forecastData?.behind_ahead === 'ahead' ? '✓ On Track' : '⚠ Behind Target'}
              color={forecastData?.behind_ahead === 'ahead' ? 'success' : 'warning'}
              size="small"
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Today's Performance */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Today's Performance
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Target
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(forecastData?.today_target)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Actual
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {formatCurrency(forecastData?.today_actual)}
                </Typography>
              </Box>
            </Box>
            
            {forecastData?.today_actual !== null && forecastData?.today_target > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Progress: {Math.round((forecastData.today_actual / forecastData.today_target) * 100)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((forecastData.today_actual / forecastData.today_target) * 100, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: forecastData.today_actual >= forecastData.today_target ? 'success.main' : 'primary.main'
                    }
                  }}
                />
              </Box>
            )}
            
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => setCurrentTab(2)}
            >
              Update Today's Actuals
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Daily Breakdown Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Daily Breakdown - Week of {formatDate(forecastData?.week_start_date)}
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Day</strong></TableCell>
                    <TableCell align="right"><strong>Forecast Revenue</strong></TableCell>
                    <TableCell align="right"><strong>Actual Revenue</strong></TableCell>
                    <TableCell align="right"><strong>Appointments</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Performance</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forecastData?.daily_breakdown?.map((day) => {
                    const performance = day.actual_revenue !== null && day.forecast_revenue > 0
                      ? Math.round((day.actual_revenue / day.forecast_revenue) * 100)
                      : null;
                    
                    return (
                      <TableRow key={day.day_of_week}>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {day.day_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(day.forecast_revenue)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={day.actual_revenue !== null ? 'text.primary' : 'text.disabled'}
                          >
                            {formatCurrency(day.actual_revenue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {day.actual_appointments || '—'} / {day.forecast_appointments}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {getPerformanceIcon(day.actual_revenue, day.forecast_revenue)}
                        </TableCell>
                        <TableCell align="right">
                          {performance !== null ? (
                            <Chip
                              label={`${performance}%`}
                              size="small"
                              color={getPerformanceColor(performance)}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              Pending
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const StaffPerformance = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Staff Performance This Week
            </Typography>
            
            {forecastData?.staff_performance && Object.keys(forecastData.staff_performance).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(forecastData.staff_performance).map(([staffName, performance]) => (
                  <Grid item xs={12} md={6} lg={4} key={staffName}>
                    <Paper sx={{ p: 3, border: '1px solid', borderColor: 'grey.200' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Person sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold">
                          {staffName}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Performance
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {performance.performance_percentage}%
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(performance.performance_percentage, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          mb: 2,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: performance.performance_percentage >= 100 ? 'success.main' : 
                                            performance.performance_percentage >= 80 ? 'warning.main' : 'error.main'
                          }
                        }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Forecast:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(performance.total_forecast)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Actual:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(performance.total_actual)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Days Complete:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {performance.days_with_actuals} / {performance.total_days}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No staff performance data available for this week.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Enhanced Quick Actions with Dynamic Forecasting
  const QuickActions = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                onClick={() => setShowSundayPlanning(true)}
                sx={{ py: 1.5 }}
              >
                Set Next Week Forecast
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Edit />}
                fullWidth
                onClick={() => setShowSundayPlanning(true)}
                sx={{ py: 1.5 }}
              >
                Edit This Week's Forecast
              </Button>
              
              <Button
                variant="contained"
                color="success"
                startIcon={<TrendingUp />}
                fullWidth
                onClick={() => setShowDailyUpdate(true)}
                sx={{ py: 1.5 }}
              >
                📈 Update Tomorrow's Forecast
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Analytics />}
                fullWidth
                onClick={() => setShowVarianceAnalysis(true)}
                sx={{ py: 1.5 }}
              >
                📊 Variance Analysis
              </Button>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Assessment />}
                fullWidth
                onClick={() => setShowPerformanceReview(true)}
                sx={{ py: 1.5 }}
              >
                Review Completed Days
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Dynamic Forecasting Tips
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper 
                sx={{ 
                  p: 2, 
                  bgcolor: 'primary.light', 
                  color: 'primary.contrastText',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 }
                }}
                onClick={() => setShowSundayPlanning(true)}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  💡 Sunday Planning
                </Typography>
                <Typography variant="body2">
                  Set your weekly forecasts every Sunday evening based on bookings and historical data.
                </Typography>
              </Paper>
              
              <Paper 
                sx={{ 
                  p: 2, 
                  bgcolor: 'success.light', 
                  color: 'success.contrastText',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 }
                }}
                onClick={() => setShowDailyUpdate(true)}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  📊 Daily Adjustments
                </Typography>
                <Typography variant="body2">
                  Update tomorrow's forecast each evening based on cancellations and new bookings.
                </Typography>
              </Paper>
              
              <Paper 
                sx={{ 
                  p: 2, 
                  bgcolor: 'warning.light', 
                  color: 'warning.contrastText',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 }
                }}
                onClick={() => setShowVarianceAnalysis(true)}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  🎯 Variance Intelligence
                </Typography>
                <Typography variant="body2">
                  Analyze booking changes vs walk-ins to understand your business patterns.
                </Typography>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Weekly Forecast Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Week of {formatDate(forecastData?.week_start_date)}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ m: 2 }}>
          {error} - Showing sample data for demonstration
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Weekly Overview" icon={<Assignment />} />
            <Tab label="Staff Performance" icon={<Person />} />
            <Tab label="Quick Actions" icon={<CheckCircle />} />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {currentTab === 0 && <WeeklyOverview />}
        {currentTab === 1 && <StaffPerformance />}
        {currentTab === 2 && <QuickActions />}
      </Box>

      {/* All Modal Components */}
      {showSundayPlanning && (
        <SundayPlanningInterface
          onClose={() => setShowSundayPlanning(false)}
          onSave={() => {
            refreshForecastData();
            setShowSundayPlanning(false);
          }}
        />
      )}

      {showPerformanceReview && (
        <EndOfDayPerformanceReview
          onClose={() => setShowPerformanceReview(false)}
        />
      )}

      {showDailyUpdate && (
        <DailyForecastUpdate
          open={showDailyUpdate}
          onClose={() => setShowDailyUpdate(false)}
          onSave={(data) => {
            console.log('Forecast revisions saved:', data);
            refreshForecastData();
          }}
        />
      )}

      {showVarianceAnalysis && (
        <VarianceAnalysisModal
          open={showVarianceAnalysis}
          onClose={() => setShowVarianceAnalysis(false)}
        />
      )}
    </Box>
  );
};

export default WeeklyForecastDashboard;